<template>
  <a-space>
    <a-tooltip 
      :title="!hasUnsavedChanges ? 'No changes to save' : ''" 
    >
      <a-button 
        type="primary" 
        :disabled="isSaving || !canSave" 
        @click="saveIntegrations" 
        :loading="isSaving"
      >
        <SaveOutlined /> Save
      </a-button>
    </a-tooltip>
  </a-space>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { message } from 'ant-design-vue'
import { SaveOutlined } from '@ant-design/icons-vue'
import { useSettingIntegrationsStore } from '@/store/modules/integrations'
import { integrationsUnsavedChanges, clearIntegrationsUnsavedChanges, useIntegrations } from '@/composables/useIntegrations'

const isSaving = ref(false)
const settingStore = useSettingIntegrationsStore()
const hasUnsavedChanges = integrationsUnsavedChanges
const { hasUserModifiedToken, hasUserModifiedPrivateToken } = useIntegrations()

// Check if we can save based on validation and changes
const canSave = computed(() => {
  // Check if there are unsaved changes - this is the primary condition
  if (!hasUnsavedChanges.value) {
    return false
  }
  
  const form = settingStore.integrationsForm
  
  // Only block save if user modified the storefront token and it's invalid
  // Allow save if user is only changing pixel IDs or other fields
  if (hasUserModifiedToken.value && form.storefrontToken && !form.isValidToken) {
    return false
  }

  if (
    hasUserModifiedPrivateToken.value &&
    form.storefrontApiPrivateToken &&
    !form.isValidPrivateToken
  ) {
    return false
  }
  
  // If there are unsaved changes, allow save
  return true
})



const saveIntegrations = async () => {
  try {
    // Validate before saving
    if (!canSave.value) {
      message.warning('Please complete the required fields before saving.')
      return
    }

    isSaving.value = true
    // Save the integrations configuration
    await settingStore.saveIntegrationsForm()
    // Clear unsaved changes flag
    clearIntegrationsUnsavedChanges()
    // Show success message
    message.success('Integrations settings saved successfully!')
  } catch (error) {
    console.error('Error saving integrations:', error)
    // Show error message
    message.error('Failed to save integrations settings. Please try again.')
  } finally {
    isSaving.value = false
  }
}
</script>
